import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Pagination, Spinner, Badge } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import axios from './utils/axiosConfig';
import { useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { isSuperAdmin } from './utils/superAdmin';
import { recordLoginInfo } from './services/recordLoginInfo';
import cacheManager from './utils/cacheManager';

function Home() {
  const { isAuthenticated, user } = useAuth();
  const [mobile, setMobile] = useState('');
  const [needMobile, setNeedMobile] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activities, setActivities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreActivities, setHasMoreActivities] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activitiesLoaded, setActivitiesLoaded] = useState(false);
  const [showClearCache, setShowClearCache] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const location = useLocation();
  const activitiesPerPage = 20;  // 增加每頁筆數，減少載入次數
  const lastActivityRef = useRef(null);  // 用於 IntersectionObserver

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const message = queryParams.get('message');
    const needMobileParam = queryParams.get('needMobile');
    const errorParam = queryParams.get('error');
    
    if (message) {
      if (errorParam) {
        const errorMsg = decodeURIComponent(message);
        setErrorMessage(errorMsg);
        
        // 如果是LINE授權錯誤，清除相關快取
        if (errorMsg.includes('授權') || errorMsg.includes('登入失敗')) {
          cacheManager.clearAuthCache();
        }
      } else {
        setSuccessMessage(decodeURIComponent(message));
      }
    }
    
    if (needMobileParam === 'true') {
      setNeedMobile(true);
    }

    // 載入活動資訊
    if (!activitiesLoaded) {
      loadActivities(1);
    }

    // 檢查是否需要顯示清除快取選項（連續點5次標題）
    let clickCount = 0;
    const handleTitleClick = () => {
      clickCount++;
      if (clickCount >= 5) {
        setShowClearCache(true);
        clickCount = 0;
      }
      setTimeout(() => { clickCount = 0; }, 2000);
    };
    
    window.handleTitleClick = handleTitleClick;
  }, [location, activitiesLoaded]);

  // 載入未來六個月活動
  const loadActivities = async (page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await axios.get(`/upcoming-activities`, {
        params: {
          page: page,
          limit: activitiesPerPage,
          months: 6
        }
      });

      if (response.data.success) {
        const newActivities = response.data.activities;
        
        if (append) {
          setActivities(prev => [...prev, ...newActivities]);
        } else {
          setActivities(newActivities);
        }
        
        setHasMoreActivities(response.data.hasMore);
        setCurrentPage(page);
        setActivitiesLoaded(true);
      }
    } catch (error) {
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  // 處理無限捲動
  const loadMoreActivities = useCallback(() => {
    if (!loadingMore && hasMoreActivities) {
      loadActivities(currentPage + 1, true);
    }
  }, [loadingMore, hasMoreActivities, currentPage]);

  // IntersectionObserver 自動載入更多（更流暢）
  useEffect(() => {
    if (!lastActivityRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreActivities && !loadingMore) {
          loadMoreActivities();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(lastActivityRef.current);
    return () => observer.disconnect();
  }, [activities, hasMoreActivities, loadingMore, loadMoreActivities]);

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '待定';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // 取得活動狀態
  const getActivityStatus = (activity) => {
    const now = new Date();
    const startDate = new Date(activity.a_Sdate);
    const endDate = new Date(activity.a_Edate);
    
    if (now < startDate) {
      return { text: '即將開始', variant: 'primary' };
    } else if (now >= startDate && now <= endDate) {
      return { text: '進行中', variant: 'success' };
    } else {
      return { text: '已結束', variant: 'secondary' };
    }
  };

  // 如果已經登入，顯示歡迎頁面
  if (isAuthenticated && user) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Body className="text-center">
                <h1 className="mb-4">
                  <i className="fas fa-calendar-check text-primary me-2"></i>
                  TWRC 活動報名系統
                </h1>
                <Alert variant="success">
                  <h4>歡迎回來，{user.e_name}！</h4>
                  <p className="mb-0">您可以開始瀏覽和報名各項活動</p>
                </Alert>
                <Row className="mt-4">
                  {/* 權限判斷和按鈕顯示邏輯 */}
                  {/* 管理活動：管理者(e_position=17)或秘書(e_id=80)可見 */}
                  {(user.e_position === 17 || isSuperAdmin(user.e_id)) && (
                    <Col lg={4} md={6} xs={6} className="mb-3">
                      <Button 
                        variant="primary" 
                        size="lg" 
                        href="/ActivitiesTable?self=true"
                        className="w-100 text-white"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.8rem',
                          fontWeight: 'bold',
                          backgroundColor: '#007bff',
                          borderColor: '#007bff',
                          padding: '0.6rem 0.5rem'
                        }}
                      >
                        <i className="fas fa-cogs mb-2" style={{fontSize: '3rem'}}></i>
                        <span style={{textAlign: 'center', lineHeight: '1.2'}}>管理活動</span>
                      </Button>
                    </Col>
                  )}
                  
                  {/* 本社活動：所有用戶都可見 */}
                  <Col lg={4} md={6} xs={6} className="mb-3">
                    <Button 
                      variant="success" 
                      size="lg" 
                      href="/Pjoinpage"
                      className="w-100 text-white"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.8rem',
                        fontWeight: 'bold',
                        backgroundColor: '#28a745',
                        borderColor: '#28a745',
                        padding: '0.6rem 0.5rem'
                      }}
                    >
                      <i className="fas fa-list mb-2" style={{fontSize: '3rem'}}></i>
                      <span style={{textAlign: 'center', lineHeight: '1.2'}}>本社活動</span>
                    </Button>
                  </Col>

                  {/* 個人資料：所有用戶都可見 */}
                  <Col lg={4} md={6} xs={6} className="mb-3">
                    <Button 
                      variant="info" 
                      size="lg" 
                      href="/Personal"
                      className="w-100 text-white"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.8rem',
                        fontWeight: 'bold',
                        backgroundColor: '#17a2b8',
                        borderColor: '#17a2b8',
                        padding: '0.6rem 0.5rem'
                      }}
                    >
                      <i className="fas fa-user mb-2" style={{fontSize: '3rem'}}></i>
                      <span style={{textAlign: 'center', lineHeight: '1.2'}}>個人資料</span>
                    </Button>
                  </Col>

                  {/* 社友名錄：管理者、秘書、社友(e_group=5)可見 */}
                  {(user.e_position === 17 || isSuperAdmin(user.e_id) || user.e_group === 5) && (
                    <Col lg={4} md={6} xs={6} className="mb-3">
                      <Button 
                        variant="warning" 
                        size="lg" 
                        href="/MemberDirectory"
                        className="w-100 text-white"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.8rem',
                          fontWeight: 'bold',
                          backgroundColor: '#ffc107',
                          borderColor: '#ffc107',
                          padding: '0.6rem 0.5rem'
                        }}
                      >
                        <i className="fas fa-address-book mb-2" style={{fontSize: '3rem'}}></i>
                        <span style={{textAlign: 'center', lineHeight: '1.2'}}>社友名錄</span>
                      </Button>
                    </Col>
                  )}

                  {/* 最近講題：管理者、秘書、社友(e_group=5)可見 */}
                  {(user.e_position === 17 || isSuperAdmin(user.e_id) || user.e_group === 5) && (
                    <Col lg={4} md={6} xs={6} className="mb-3">
                      <Button 
                        variant="danger" 
                        size="lg" 
                        href="/TopicsTable"
                        className="w-100 text-white"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.8rem',
                          fontWeight: 'bold',
                          backgroundColor: '#dc3545',
                          borderColor: '#dc3545',
                          padding: '0.6rem 0.5rem'
                        }}
                      >
                        <i className="fas fa-lightbulb mb-2" style={{fontSize: '3rem'}}></i>
                        <span style={{textAlign: 'center', lineHeight: '1.2'}}>最近講題</span>
                      </Button>
                    </Col>
                  )}

                  {/* 工商參訪：管理者、秘書、社友(e_group=5)可見 */}
                  {(user.e_position === 17 || isSuperAdmin(user.e_id) || user.e_group === 5) && (
                    <Col lg={4} md={6} xs={6} className="mb-3">
                      <Button 
                        variant="info" 
                        size="lg" 
                        href="/CompanyTable"
                        className="w-100 text-white"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.8rem',
                          fontWeight: 'bold',
                          backgroundColor: '#17a2b8',
                          borderColor: '#17a2b8',
                          padding: '0.6rem 0.5rem'
                        }}
                      >
                        <i className="fas fa-building mb-2" style={{fontSize: '3rem'}}></i>
                        <span style={{textAlign: 'center', lineHeight: '1.2'}}>工商參訪</span>
                      </Button>
                    </Col>
                  )}

                  {/* 社友分析：管理者、秘書、社友(e_group=5)可見 */}
                  {(user.e_position === 17 || isSuperAdmin(user.e_id) || user.e_group === 5) && (
                    <Col lg={4} md={6} xs={6} className="mb-3">
                      <Button
                        variant="dark"
                        size="lg"
                        href="/MemberAnalytics"
                        className="w-100 text-white"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.8rem',
                          fontWeight: 'bold',
                          backgroundColor: '#343a40',
                          borderColor: '#343a40',
                          padding: '0.6rem 0.5rem'
                        }}
                      >
                        <i className="fas fa-chart-pie mb-2" style={{fontSize: '3rem'}}></i>
                        <span style={{textAlign: 'center', lineHeight: '1.2'}}>社友分析</span>
                      </Button>
                    </Col>
                  )}

                  {/* 抽獎工具：所有登入用戶可見 */}
                  <Col lg={4} md={6} xs={6} className="mb-3">
                    <Button
                      size="lg"
                      href="https://wheelofnames.com/zh-HK/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-100 text-white"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.8rem',
                        fontWeight: 'bold',
                        backgroundColor: '#ff6b6b',
                        borderColor: '#ff6b6b',
                        padding: '0.6rem 0.5rem'
                      }}
                    >
                      <i className="fas fa-gift mb-2" style={{fontSize: '3rem'}}></i>
                      <span style={{textAlign: 'center', lineHeight: '1.2'}}>抽獎工具</span>
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 登入後也顯示活動預覽區域 */}
        <Row className="mt-5">
          <Col>
            <Card>
              <Card.Header className="bg-primary text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <h4 className="mb-0">
                    <i className="fas fa-calendar-alt me-2"></i>
                    近期各社活動預覽
                  </h4>
                  <small>未來三個月活動</small>
                </div>
              </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">載入活動資訊中...</p>
                </div>
              ) : activities.length > 0 ? (
                <>
                  <Row>
                    {activities.map((activity, index) => {
                      const status = getActivityStatus(activity);
                      const isLast = index === activities.length - 1;
                      return (
                        <Col
                          md={6}
                          lg={4}
                          key={activity.a_id}
                          className="mb-4"
                          ref={isLast ? lastActivityRef : null}
                        >
                          <Card className="h-100 shadow-sm">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1">
                                  <Badge bg={status.variant} className="mb-2">{status.text}</Badge>
                                  <Card.Title className="h6 mb-2">
                                    {activity.a_name || '活動名稱未定'}
                                  </Card.Title>
                                  <Card.Text className="small text-muted mb-2">
                                    <i className="fas fa-calendar me-1"></i>
                                    {activity.a_Sdate ? formatDate(activity.a_Sdate) : '日期待定'}
                                    {activity.a_Edate && activity.a_Edate !== activity.a_Sdate && 
                                      ` ~ ${formatDate(activity.a_Edate)}`
                                    }
                                  </Card.Text>
                                  {activity.a_location && (
                                    <Card.Text className="small text-muted mb-2">
                                      <i className="fas fa-map-marker-alt me-1"></i>
                                      <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.a_location)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#17a2b8' }}
                                      >
                                        {activity.a_location}
                                      </a>
                                    </Card.Text>
                                  )}
                                  {activity.a_people && (
                                    <Card.Text className="small text-muted mb-2">
                                      <i className="fas fa-users me-1"></i>
                                      限額：{activity.a_people} 人
                                    </Card.Text>
                                  )}
                                </div>
                                <div className="text-end" style={{ minWidth: '120px' }}>
                                  <small className="text-muted d-block mb-2">{activity.store_name || '未知社別'}</small>
                                  {activity.a_EDM && (
                                    <img 
                                      src={`/uploads/AEDM/${activity.a_EDM}?t=${new Date().getTime()}`} 
                                      alt={activity.a_name}
                                      style={{ 
                                        width: '100px', 
                                        height: '141px', // A4比例 (100 * 1.41)
                                        objectFit: 'cover',
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        border: '1px solid #ddd'
                                      }}
                                      onClick={() => setLightboxImage(`/uploads/AEDM/${activity.a_EDM}?t=${new Date().getTime()}`)}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>

                  {/* 載入更多指示器 */}
                  {loadingMore && (
                    <div className="text-center py-4">
                      <Spinner animation="border" variant="primary" size="sm" />
                      <p className="mt-2 text-muted">載入更多活動中...</p>
                    </div>
                  )}
                  
                  {!hasMoreActivities && activities.length > 0 && (
                    <div className="text-center py-4">
                      <p className="text-muted">已顯示所有活動</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">暫無近期活動</h5>
                  <p className="text-muted">請稍後再檢查或聯絡管理員</p>
                </div>
              )}
            </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 圖片燈箱 */}
        {lightboxImage && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999
            }}
            onClick={() => setLightboxImage(null)}
          >
            <img
              src={lightboxImage}
              alt="活動海報"
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain'
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.8)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              onClick={() => setLightboxImage(null)}
            >
              ×
            </button>
          </div>
        )}
      </Container>
    );
  }


  // LINE 授權登入邏輯
  const handleLineLogin = () => {
    const clientId = process.env.REACT_APP_LINE_CHANNEL_ID || '2000402227';
    const redirectUri = encodeURIComponent(process.env.REACT_APP_LINE_REDIRECT_URI || 'https://twrc.li.com.tw/callback');
    
    let state = 'login_only';
    if (needMobile && mobile) {
      state = `mobile_${mobile}`;
    }
    
    const lineAuthUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&scope=profile%20openid`;
    window.location.href = lineAuthUrl;
  };

  // 手機號綁定提交
  const handleMobileBinding = (e) => {
    e.preventDefault();
    if (!mobile.trim()) {
      setErrorMessage('請輸入手機號碼');
      return;
    }
    handleLineLogin();
  };

  // 未登入時顯示登入介面
  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card>
            <Card.Body>
              <div className="text-center mb-4">
                <h1 className="mb-3" onClick={() => window.handleTitleClick && window.handleTitleClick()}>
                  <i className="fas fa-calendar-check text-primary me-2"></i>
                  TWRC 活動報名
                </h1>
                <p className="text-muted">請先登入以報名參加活動</p>
              </div>

              {successMessage && (
                <Alert variant="success" className="text-center">
                  {successMessage}
                </Alert>
              )}

              {errorMessage && (
                <Alert variant="danger" className="text-center">
                  <div>{errorMessage}</div>
                  {errorMessage.includes('授權') && (
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      className="mt-2"
                      onClick={async () => {
                        await cacheManager.forceRefreshAuth();
                      }}
                    >
                      <i className="fas fa-refresh me-1"></i>
                      清除快取並重試
                    </Button>
                  )}
                </Alert>
              )}

              {/* 緊急清除快取按鈕 (隱藏功能) */}
              {showClearCache && (
                <Alert variant="warning" className="text-center">
                  <h6>🛠️ 開發者工具</h6>
                  <div className="d-grid gap-2">
                    <Button 
                      variant="warning" 
                      onClick={async () => {
                        try {
                          // 清除所有 localStorage 和 sessionStorage
                          localStorage.clear();
                          sessionStorage.clear();
                          
                          // 清除所有快取
                          if ('caches' in window) {
                            const cacheNames = await caches.keys();
                            await Promise.all(
                              cacheNames.map(name => caches.delete(name))
                            );
                          }
                          
                          alert('✅ 所有快取已清除！即將重新載入...');
                          window.location.reload(true);
                        } catch (error) {
                          alert('❌ 清除快取失敗：' + error.message);
                        }
                      }}
                    >
                      🧹 清除所有快取並重新載入
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => setShowClearCache(false)}
                    >
                      隱藏工具
                    </Button>
                  </div>
                </Alert>
              )}

              {/* 手機號綁定表單 */}
              {needMobile && (
                <div>
                  <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle"></i> 新用戶需要綁定手機號
                  </Alert>
                  <Form onSubmit={handleMobileBinding}>
                    <Form.Group controlId="formMobileBinding" className="mb-3">
                      <Form.Label>請輸入您的手機號碼：</Form.Label>
                      <Form.Control
                        type="tel"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        placeholder="例如：0912345678"
                        required
                      />
                      <Form.Text className="text-muted">
                        請輸入您在系統中註冊的手機號碼進行 LINE 綁定
                      </Form.Text>
                    </Form.Group>
                    <Button variant="success" type="submit" className="w-100 mb-3">
                      <i className="fab fa-line"></i> 綁定 LINE 並登入
                    </Button>
                  </Form>
                  <div className="text-center">
                    <Button 
                      variant="link" 
                      onClick={() => {
                        setNeedMobile(false);
                        setErrorMessage('');
                      }}
                    >
                      返回登入頁面
                    </Button>
                  </div>
                </div>
              )}

              {/* 主要登入介面 */}
              {!needMobile && (
                <div>
                  {/* 主要 LINE 登入按鈕 */}
                  <div className="text-center mb-4">
                    <Button 
                      variant="success" 
                      size="lg" 
                      className="w-100"
                      onClick={handleLineLogin}
                      style={{ 
                        padding: '15px', 
                        fontSize: '18px',
                        fontWeight: 'bold'
                      }}
                    >
                      <i className="fab fa-line me-2"></i> 使用 LINE 快速登入
                    </Button>
                    <div className="mt-2">
                      <small className="text-muted">快速登入，立即報名活動</small>
                    </div>
                  </div>
                </div>
              )}

            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 活動預覽區域 */}
      <Row className="mt-5">
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <i className="fas fa-calendar-alt me-2"></i>
                  近期各社活動預覽
                </h4>
                <small>未來三個月活動</small>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">載入活動資訊中...</p>
                </div>
              ) : activities.length > 0 ? (
                <>
                  <Row>
                    {activities.map((activity, index) => {
                      const status = getActivityStatus(activity);
                      const isLast = index === activities.length - 1;
                      return (
                        <Col
                          md={6}
                          lg={4}
                          key={activity.a_id}
                          className="mb-4"
                          ref={isLast ? lastActivityRef : null}
                        >
                          <Card className="h-100 shadow-sm">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="flex-grow-1">
                                  <Badge bg={status.variant} className="mb-2">{status.text}</Badge>
                                  <Card.Title className="h6 mb-2">
                                    {activity.a_name || '活動名稱未定'}
                                  </Card.Title>
                                  <Card.Text className="small text-muted mb-2">
                                    <i className="fas fa-calendar me-1"></i>
                                    {activity.a_Sdate ? formatDate(activity.a_Sdate) : '日期待定'}
                                    {activity.a_Edate && activity.a_Edate !== activity.a_Sdate && 
                                      ` ~ ${formatDate(activity.a_Edate)}`
                                    }
                                  </Card.Text>
                                  {activity.a_location && (
                                    <Card.Text className="small text-muted mb-2">
                                      <i className="fas fa-map-marker-alt me-1"></i>
                                      <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.a_location)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#17a2b8' }}
                                      >
                                        {activity.a_location}
                                      </a>
                                    </Card.Text>
                                  )}
                                  {activity.a_people && (
                                    <Card.Text className="small text-muted mb-2">
                                      <i className="fas fa-users me-1"></i>
                                      限額：{activity.a_people} 人
                                    </Card.Text>
                                  )}
                                </div>
                                <div className="text-end" style={{ minWidth: '120px' }}>
                                  <small className="text-muted d-block mb-2">{activity.store_name || '未知社別'}</small>
                                  {activity.a_EDM && (
                                    <img 
                                      src={`/uploads/AEDM/${activity.a_EDM}?t=${new Date().getTime()}`} 
                                      alt={activity.a_name}
                                      style={{ 
                                        width: '100px', 
                                        height: '141px', // A4比例 (100 * 1.41)
                                        objectFit: 'cover',
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        border: '1px solid #ddd'
                                      }}
                                      onClick={() => setLightboxImage(`/uploads/AEDM/${activity.a_EDM}?t=${new Date().getTime()}`)}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>

                  {/* 載入更多指示器 */}
                  {loadingMore && (
                    <div className="text-center py-4">
                      <Spinner animation="border" variant="primary" size="sm" />
                      <p className="mt-2 text-muted">載入更多活動中...</p>
                    </div>
                  )}
                  
                  {!hasMoreActivities && activities.length > 0 && (
                    <div className="text-center py-4">
                      <p className="text-muted">已顯示所有活動</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-calendar-times fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">暫無近期活動</h5>
                  <p className="text-muted">請稍後再檢查或聯絡管理員</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            cursor: 'pointer'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <img 
            src={lightboxImage}
            alt="活動海報"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255, 255, 255, 0.8)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
            onClick={() => setLightboxImage(null)}
          >
            ×
          </button>
        </div>
      )}
    </Container>
  );
}

export default Home;
